import loadable from '@loadable/component';
import React from 'react';
import { useSelector } from 'react-redux';

import { mdp_area_hero } from 'media';

import useDeviceType, {
  DESKTOP_SCREEN,
  MOBILE_SCREEN,
} from '@travel/traveler-core/hooks/useDeviceType';
import isNotEmptyArray from '@travel/ui/utils/arrays';

import Breadcrumb from 'components/Breadcrumb';
import DataLayer from 'components/DataLayer';
import EmailSubscription from 'components/EmailSubscription';
import useSubscriptionScreenType from 'components/EmailSubscription/useSubscriptionScreenType';
import HeroImage from 'components/HeroImage';
import OtherAreas from 'components/OtherAreas';
import PromotionBanners from 'components/PromotionBanners';

import {
  getAirportAreas,
  getAreaName,
  getChildAreas,
  getHeroImage,
  getHotSprings,
  getIsFetching as getIsAreaFetching,
  getItems,
  getParentAreaName,
  getSiblingAreas,
} from 'store/areaDetails/selectors';
import { getIsFetching as getIsBannersFetching } from 'store/promotionBanners/selectors';
import { getIsFetching as getIsSpecialArticlesFetching } from 'store/specialFeatureArticles/selectors';
import { getIsFetching as getIsSubscriptionFetching } from 'store/subscription/selectors';

import { SUBSCRIPTION_LOCATION } from 'constants/subscriptionLocation';
import { Translate } from 'core/translate';
import usePageLoaderOnInitial from 'hooks/usePageLoaderOnInitial';
import type { SearchFormQuery } from 'ProviderList-Types';

import Recommendations from '../TopPage/components/Recommendations';
import SpecialFeatureArticles from '../TopPage/components/SpecialFeatureArticles';
import RecommendedProviders from './components/RecommendedProviders';
import SearchBar from './components/SearchBar';

import styles from './areaPage.module.scss';

const AppDownloadBanner = loadable(
  () =>
    import(
      /* webpackMode: "lazy" */
      /* webpackFetchPriority: "low" */
      /* webpackChunkName: "component-AppDownloadBanner" */
      '../TopPage/components/AppDownloadBanner'
    ),
  { ssr: false },
);

const FALLBACK_IMAGE_HEROIMAGE = [
  {
    url: mdp_area_hero,
  },
];

type Props = {
  params: {
    pathId: string;
  };
  query: SearchFormQuery;
};

function AreaPage(props: Props) {
  const screenType = useSubscriptionScreenType();
  const deviceType = useDeviceType();
  const isMobile = deviceType === MOBILE_SCREEN;
  const isDesktop = deviceType === DESKTOP_SCREEN;

  const area = useSelector(getItems);
  const areaName = useSelector(getAreaName);
  const parentAreaName = useSelector(getParentAreaName);
  const heroImage = useSelector(getHeroImage);
  const otherChildren = useSelector(getChildAreas);
  const otherSiblings = useSelector(getSiblingAreas);
  const otherHotSprings = useSelector(getHotSprings);
  const otherAirports = useSelector(getAirportAreas);

  const isCategoryCountry = area?.category === 'COUNTRY';
  const isHeroImagePresent = Array.isArray(heroImage) && heroImage.some((image) => image.url);
  const heroImageToUse = isHeroImagePresent ? heroImage : FALLBACK_IMAGE_HEROIMAGE;

  // page loader
  const isAreaFetching = useSelector(getIsAreaFetching);
  const isSpecialArticlesFetching = useSelector(getIsSpecialArticlesFetching);
  const isBannersFetching = useSelector(getIsBannersFetching);
  const isSubscriptionFetching = useSelector(getIsSubscriptionFetching);
  usePageLoaderOnInitial(
    'area',
    isAreaFetching || isSpecialArticlesFetching || isBannersFetching || isSubscriptionFetching,
  );

  const providerIdList = area.recommendedProviders
    ? area.recommendedProviders.filter((item) => item).map((item) => item.id)
    : null;

  const ratArea = area.breadcrumb
    ? area.breadcrumb
        .filter((item) => item)
        .map((item) => item.name)
        .join(',')
    : '';

  return (
    <div data-testid="areaPage-page-wrapper">
      <HeroImage
        page="Area"
        images={heroImageToUse}
        customTestId={isHeroImagePresent ? '' : 'heroImage-fallback-mediaWrapper'}
      />

      <div className={styles.contentWrapper}>
        <SearchBar
          currentPlaceName={area.areaName}
          query={props.query}
          pageIdentifier={props.params.pathId}
        />

        {!isCategoryCountry && <RecommendedProviders pathId={props.params.pathId} />}

        <Recommendations
          title={
            <Translate id="AreaTop.Recommended_Areas.Title" data={{ area_name: areaName || '-' }} />
          }
          page="Area"
        />
        <PromotionBanners />
        <SpecialFeatureArticles title={<Translate id="AreaTop.Special_Features.Title" />} />

        <div className={styles.appBannerAndEmailSubsWrapper}>
          {isDesktop && <AppDownloadBanner />}
          {screenType && (
            <EmailSubscription location={SUBSCRIPTION_LOCATION.AREA_TOP} screenType={screenType} />
          )}
        </div>

        {!isMobile && (
          <h2 className={styles.areaTitle}>
            <Translate id="AreaTop.Other_Recommendations.Title" />
          </h2>
        )}

        <div className={styles.otherAreasSection}>
          {isCategoryCountry ? (
            <>
              {isNotEmptyArray(otherChildren) && (
                <OtherAreas
                  title={
                    <Translate
                      id="AreaTop.Other_Subdivision.Title"
                      data={{ country_name: areaName }}
                    />
                  }
                  type="SUBDIVISIONS"
                  otherAreas={otherChildren}
                />
              )}

              {isNotEmptyArray(otherSiblings) && (
                <OtherAreas
                  title={
                    <Translate
                      id="AreaTop.Out_To_Out.Other_Countries.Title"
                      data={{ continent_name: parentAreaName }}
                    />
                  }
                  type="COUNTRIES"
                  otherAreas={otherSiblings}
                />
              )}
            </>
          ) : (
            <>
              {isNotEmptyArray(otherChildren) && (
                <OtherAreas
                  title={
                    <Translate
                      id="AreaTop.Other_Cities.Title"
                      data={{ subdivision_name: areaName }}
                    />
                  }
                  type="CITIES"
                  otherAreas={otherChildren}
                />
              )}

              {isNotEmptyArray(otherSiblings) && (
                <OtherAreas
                  title={
                    <Translate
                      id="AreaTop.Other_Subdivision.Title"
                      data={{ country_name: parentAreaName }}
                    />
                  }
                  type="SUBDIVISIONS"
                  otherAreas={otherSiblings}
                />
              )}

              {isNotEmptyArray(otherHotSprings) && (
                <OtherAreas
                  title={
                    <Translate
                      id="AreaTop.Hot_Springs.Title"
                      data={{ subdivision_name: areaName }}
                    />
                  }
                  type="HOTSPRINGS"
                  otherAreas={otherHotSprings}
                />
              )}

              {isNotEmptyArray(otherAirports) && (
                <OtherAreas
                  title={
                    <Translate id="AreaTop.Airports.Title" data={{ subdivision_name: areaName }} />
                  }
                  type="AIRPORTS"
                  otherAreas={otherAirports}
                />
              )}
            </>
          )}
        </div>
        {!isMobile && <Breadcrumb className={styles.breadcrumb} placeLayers={area.breadcrumb} />}
      </div>

      <DataLayer
        pageName="area:search_form"
        pageType="top"
        siteSection="area"
        placeName={
          area?.areaInfo?.city ||
          area?.areaInfo?.district ||
          area?.areaInfo?.region ||
          area?.areaInfo?.country
        }
        areaName={ratArea}
        propertyCountry={area?.areaInfo?.country}
        hotelRegion={area?.areaInfo?.region}
        propertyCity={area?.areaInfo?.city}
        propertyDistrict={area?.areaInfo?.district}
        shopIdList={providerIdList?.join(',')}
      />
    </div>
  );
}

export default AreaPage;
